import { originalConsoleError } from "../../../utils/suppressConsoleMethods";

interface AppRouterBoundaryError extends Error {
  digest?: string;
}

const LOG_ERROR_PATH = "/app-router/log-error";

// The beacon must carry the app's runtime asset prefix ("/uf", or "/{env}/uf"
// in multi-env deploys). The Express receiver registers BOTH `${root}/...` and
// the bare path, but at the edge Akamai only routes the prefixed path to this
// origin — a bare "/app-router/log-error" 404s in dev1/test/prod (it passes
// locally only because root === "" and the nginx catch-all proxies everything).
// This mirrors the sibling RUM beacon (src/utils/rum/inpCapture.ts), which
// prefixes for exactly this reason. Kept self-contained so this error-path
// module pulls in no other runtime.
let cachedPrefix: string | null = null;

// Drop a leading scheme+host (absolute or protocol-relative) so the beacon
// stays same-origin even when assetPrefix is an absolute CDN URL.
function stripOrigin(value: string): string {
  return value.replace(/^(?:https?:)?\/\/[^/]+/, "");
}

function stripTrailingSlashes(value: string): string {
  return value.replace(/\/+$/, "");
}

// Prefer the Pages-Router __NEXT_DATA__.assetPrefix when present (authoritative);
// otherwise derive it from an already-loaded /_next/ asset URL, which both
// routers emit (App Router has no __NEXT_DATA__). Returns "" for local root.
function detectAssetPrefix(): string {
  if (cachedPrefix !== null) {
    return cachedPrefix;
  }
  let prefix = "";
  try {
    const nextData =
      // eslint-disable-next-line no-underscore-dangle
      typeof window !== "undefined" ? (window as any).__NEXT_DATA__ : undefined;
    const fromData = nextData && nextData.assetPrefix;
    if (typeof fromData === "string" && fromData) {
      prefix = stripTrailingSlashes(stripOrigin(fromData));
    } else if (typeof document !== "undefined") {
      const el =
        document.querySelector('script[src*="/_next/"]') ||
        document.querySelector('link[href*="/_next/"]');
      const assetUrl =
        (el && (el.getAttribute("src") || el.getAttribute("href"))) || "";
      const marker = assetUrl.indexOf("/_next/");
      if (marker > 0) {
        prefix = stripTrailingSlashes(stripOrigin(assetUrl.slice(0, marker)));
      }
    }
  } catch {
    prefix = "";
  }
  cachedPrefix = prefix;
  return cachedPrefix;
}

/**
 * Called from every app/v2/*\/error.tsx and app/error.tsx boundary.
 *
 * - Logs via the console.error reference captured before
 *   suppressConsoleMethods() can silence it (WAT-10904 AC4).
 * - POSTs to the server so the error reaches server stdout even when the
 *   boundary itself runs entirely client-side (WAT-10904 AC3). Reporting is
 *   best-effort and swallows its own failures — a broken beacon must never
 *   surface as an unhandled rejection inside an error boundary.
 */
export function reportAppRouterBoundaryError(
  scope: string,
  error: AppRouterBoundaryError
): void {
  originalConsoleError(`[${scope}]`, {
    message: error?.message,
    digest: error?.digest,
    name: error?.name
  });

  try {
    const endpoint = `${detectAssetPrefix()}${LOG_ERROR_PATH}`;
    const payload = JSON.stringify({
      scope,
      message: error?.message,
      name: error?.name,
      stack: error?.stack,
      digest: error?.digest
    });

    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(
        endpoint,
        new Blob([payload], { type: "application/json" })
      );
      return;
    }

    if (typeof fetch === "function") {
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true
      }).catch(() => { /* best-effort — see comment above */ });
    }
  } catch { /* best-effort — see comment above */ }
}
